'use client'

// Your jobs (Sam, 30 Oct: "Instead of a your pay tab, have your jobs and they can see the jobs they have taken", and
// "Yes to light page for your jobs - but also keep it as a tab on the main page too"): the job you have on, every job
// you've taken with its own page and how it went, what you're owed and claiming it. The same list is the overlay's
// tab in the town (JobCentreHud) and the light page at /jobs/mine, which is where the emails send people to claim pay.
// Pay is claimed only signed in: a job page's link moves no money.

import { useState } from 'react'
import { authFetch } from '../auth/useUser'
import { look, Btn, Label, Small, Led, field } from './look'

export const money = (p) => (p >= 100 ? `£${(p / 100).toFixed(p % 100 ? 2 : 0)}` : `${p}p`)
const clock = (t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
export function whenAt(t) {
  const d = new Date(t)
  if (d.toDateString() === new Date().toDateString()) return clock(t)
  if (d.toDateString() === new Date(Date.now() + 864e5).toDateString()) return `tomorrow at ${clock(t)}`
  return `${d.toLocaleDateString([], { weekday: 'long' })} at ${clock(t)}`
}
export function left(ms) {
  if (ms <= 0) return 'TIME UP'
  const m = Math.ceil(ms / 60000)
  return m >= 60 ? `${Math.floor(m / 60)}H ${String(m % 60).padStart(2, '0')}M` : `${m} MIN`
}
export const jobPage = (token) => `/job/${token}`

// what each way of being paid says; when it reaches you is the town's to say (board.payTimes: bank on Fridays, SOL
// within the hour once the town sends it by itself)
export const HOW = [
  { id: 'credit', name: 'Shop credit', say: () => 'A code for his shop, there and then. Any amount.' },
  { id: 'bank', name: 'Bank transfer', say: (t) => `To a UK bank account. ${t?.bank || 'Bank transfers are paid on Fridays.'} Your details are deleted once it's paid.` },
  { id: 'sol', name: 'SOL', say: (t) => `To your Solana address. ${t?.sol || 'It reaches you in 1 to 3 working days.'}` },
]
// how a job went, said to whoever did it
export const STATE_SAYS = { open: 'On now', sent: 'Sent, being checked', paid: 'Passed', refused: 'Didn\'t pass', lapsed: 'Ran out of time', dropped: 'Given back', reversed: 'Taken back' }
// where its pay is
export function payNow(pay, now = Date.now()) {
  if (!pay) return ''
  if (pay.state === 'owed') return pay.availableAt > now ? `${pay.label}, yours to claim from ${whenAt(pay.availableAt)}` : `${pay.label}, yours to claim now`
  if (pay.state === 'asked') return `${pay.label}, claimed and on its way`
  if (pay.state === 'withdrawn') return `${pay.label}, paid`
  if (pay.state === 'void') return 'not owed: the job was taken back'
  return pay.label
}

/** The list itself. `mine` is /api/jobs/me; `onChanged` reads it again. */
export function YourJobs({ mine, holdHours = 24, payTimes = null, onChanged, now = Date.now() }) {
  const [how, setHow] = useState('credit')
  const [to, setTo] = useState({ name: '', sort: '', account: '', sol: '' })
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState(null)
  if (!mine?.signedIn) return null
  const owed = mine.owed
  const howNow = HOW.find((h) => h.id === how)
  const claim = mine.claim

  const claimPay = async () => {
    setBusy(true); setSaid(null)
    const dest = how === 'bank' ? { name: to.name, sort: to.sort, account: to.account } : how === 'sol' ? to.sol : ''
    try {
      const res = await authFetch('/api/jobs/withdraw', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ how, to: dest }) })
      const j = await res.json().catch(() => null)
      if (!res.ok || j?.ok === false) setSaid({ bad: true, text: j?.message || 'That didn\'t work. Try again.' })
      else {
        setTo({ name: '', sort: '', account: '', sol: '' })
        setSaid({ text: j.code ? `Your code for his shop: ${j.code}. It's emailed to you and kept below.` : `Claimed. ${j.withdrawal?.reaches || ''} You'll get an email when it's sent.` })
      }
    } finally { setBusy(false); onChanged?.() }
  }

  return (
    <div style={{ color: look.ink }}>
      {said && <div style={{ padding: '9px 11px', marginBottom: 12, border: `2px solid ${said.bad ? look.stamp : look.sign}`, background: '#fff', fontFamily: look.sans, fontWeight: 700, fontSize: 13, color: said.bad ? look.stamp : look.sign }}>{said.text}</div>}

      {claim && (
        <div style={{ background: '#fff', border: `2px dashed ${look.ink}`, padding: 12, marginBottom: 12, fontFamily: look.type }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: look.sans, fontWeight: 900, letterSpacing: '0.1em', fontSize: 12 }}>ON NOW · {claim.job?.label}</div>
            <Led label="Left" value={left(claim.dueAt - now)} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, margin: '8px 0' }}>{claim.job?.ask || claim.job?.text}</div>
          {claim.token && <a href={jobPage(claim.token)} style={{ fontFamily: look.sans, fontWeight: 800, fontSize: 13, color: look.sign }}>Open your job page</a>}
        </div>
      )}

      <div style={{ background: '#fff', border: `1.5px solid ${look.inkSoft}`, padding: 12 }}>
        <Label>Owed to you</Label>
        <div style={{ fontFamily: look.type, fontSize: 26, fontWeight: 900 }}>{money(owed?.total || 0)}</div>
        <Small>{owed?.now ? `${money(owed.now)} you can claim now.` : 'None of it can be claimed yet.'}{owed?.next ? ` ${money(owed.next.pence)} more from ${whenAt(owed.next.at)}.` : ''} Pay can be claimed {holdHours} hours after a picture passes.</Small>
      </div>

      {owed?.now > 0 && (
        <div style={{ background: '#fff', border: `1.5px solid ${look.inkSoft}`, padding: 12, marginTop: 12 }}>
          <div style={{ fontFamily: look.sans, fontWeight: 900, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Claim your pay</div>
          <Label>How</Label>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {HOW.map((h) => <Btn key={h.id} small primary={how === h.id} onClick={() => setHow(h.id)}>{h.name}</Btn>)}
          </div>
          <Small style={{ marginTop: 6 }}>{howNow.say(payTimes)}{how !== 'credit' && owed.min ? ` ${money(owed.min[how])} at least.` : ''}</Small>
          {how === 'bank' && (
            <>
              <Label>Name on the account</Label>
              <input style={field} value={to.name} onChange={(e) => setTo({ ...to, name: e.target.value })} autoComplete="name" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8 }}>
                <div><Label>Sort code</Label><input style={field} value={to.sort} onChange={(e) => setTo({ ...to, sort: e.target.value })} inputMode="numeric" placeholder="00-00-00" /></div>
                <div><Label>Account number</Label><input style={field} value={to.account} onChange={(e) => setTo({ ...to, account: e.target.value })} inputMode="numeric" placeholder="00000000" /></div>
              </div>
            </>
          )}
          {how === 'sol' && (<><Label>Solana address</Label><input style={field} value={to.sol} onChange={(e) => setTo({ ...to, sol: e.target.value })} spellCheck={false} /></>)}
          <div style={{ marginTop: 10 }}><Btn primary full disabled={busy} onClick={claimPay}>{busy ? '...' : `Claim ${money(owed.now)}`}</Btn></div>
        </div>
      )}

      {mine.withdrawals?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Label>Pay claimed</Label>
          {mine.withdrawals.map((w) => (
            <Small key={w.id}>{whenAt(w.at)}: {w.label} as {HOW.find((h) => h.id === w.how)?.name.toLowerCase() || w.how}{w.to ? ` (${w.to})` : ''}, {w.state === 'paid' ? (w.code ? `your code: ${w.code}` : 'paid') : w.state === 'asked' ? `on its way. ${w.reaches || ''}` : 'didn\'t go through, so it\'s still yours to claim'}</Small>
          ))}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <Label>Jobs you've taken</Label>
        {!claim && !mine.history?.length && <Small>None yet. Take one from the vacancies.</Small>}
        <div style={{ display: 'grid', gap: 8 }}>
          {mine.history?.map((c) => (
            <div key={c.id} style={{ background: '#fff', border: `1.5px solid ${look.wallDeep}`, padding: '9px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: look.sans, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: look.inkSoft }}>
                <span>{STATE_SAYS[c.state] || c.state}</span><span>{c.job?.label}</span>
              </div>
              <div style={{ fontFamily: look.type, fontSize: 13.5, fontWeight: 700, margin: '5px 0 3px' }}>{c.job?.text}</div>
              {c.reason && <Small>{c.reason}</Small>}
              {c.pay && <Small>Pay: {payNow(c.pay, now)}.</Small>}
              {c.hisLine && <div style={{ fontFamily: look.type, fontSize: 13, fontStyle: 'italic', marginTop: 4, color: look.sign }}>Pino: “{c.hisLine}”</div>}
              {c.token && <a href={jobPage(c.token)} style={{ display: 'inline-block', marginTop: 5, fontFamily: look.sans, fontWeight: 800, fontSize: 12, color: look.sign }}>Its job page</a>}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default YourJobs
